import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time exceptions so one bad field cannot blank the console.
 *
 * Without this, a single unguarded property access (an environment whose
 * `semantic_tags` the API hasn't produced yet, say) unmounts the entire React
 * tree and the user is left staring at a white page with no way back.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[RoboTwins] render error:", error, info.componentStack);
  }

  handleReset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div style={{ padding: 40, maxWidth: 680, margin: "0 auto" }}>
        <div className="card" style={{ borderColor: "var(--color-danger)" }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>
            This screen hit an error
          </h2>
          <p className="text-muted" style={{ marginBottom: 16 }}>
            The rest of the console is still running. You can go back and
            continue working, or reload to start fresh.
          </p>
          <pre
            style={{
              background: "var(--color-bg-secondary)",
              padding: "10px 12px",
              fontSize: 12,
              overflowX: "auto",
              marginBottom: 18,
            }}
          >
            {error.message}
          </pre>
          <div className="flex gap-2">
            <button
              className="btn btn-secondary"
              onClick={() => {
                this.handleReset();
                window.history.back();
              }}
            >
              Go back
            </button>
            <button
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              Reload console
            </button>
          </div>
        </div>
      </div>
    );
  }
}
